import { useCallback, useState } from "react";
import { Eye, FileSpreadsheet, FileText, RotateCcw, SlidersHorizontal, PenLine } from "lucide-react";
import { PackPreview } from "@/components/PackPreview";
import { SignOffDialog } from "@/components/SignOffDialog";
import { Button } from "@/components/ui/button";
import { api, downloadFile } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";

export const ExportBar = ({ projectId, project, onClearOverrides, onEditAssumptions, hasOverrides, onProjectChanged }) => {
  const [busy, setBusy] = useState("");
  const [preview, setPreview] = useState(false);
  const [signKind, setSignKind] = useState(null);
  const { can } = useAuth();
  const prepare = useCallback(async () => {
    let st = (await api.post(`/projects/${projectId}/export/prepare`)).data;
    if (!st.ready) toast.info("Preparing the Budget Pack narrative — this takes about a minute the first time.");
    for (let i = 0; i < 60 && !st.ready; i++) {
      await new Promise((r) => setTimeout(r, 3000));
      st = (await api.get(`/projects/${projectId}/export/prepare`)).data;
      if (!st.ready && !st.running) break;
    }
  }, [projectId]);
  const dl = async (kind, detail = false, draft = false) => {
    setBusy(kind + (detail ? "-detail" : ""));
    try {
      if (!detail) await prepare();
      await downloadFile(`/projects/${projectId}/export/${detail ? "detail/" : ""}${kind}`, `${detail ? "budget-detail" : "budget-pack"}.${kind}`);
      toast.success(detail ? "Detailed workings downloaded" : `Owner budget pack (${kind.toUpperCase()}) downloaded${draft ? " as DRAFT" : ""}`);
    } catch (e) {
      toast.error(e.message);
    } finally {
      setBusy("");
    }
  };
  const confirm = (kind, draft) => { if (kind === "preview") setPreview(true); else dl(kind, false, draft); };
  const signed = project && project.prepared_by && project.approved_by && project.acknowledged_by;
  return (
    <div className="flex flex-wrap items-center gap-2" data-testid="export-bar">
      {can("edit") && onEditAssumptions && <Button variant="outline" size="sm" onClick={onEditAssumptions} data-testid="edit-assumptions-button"><SlidersHorizontal size={14} className="mr-1.5" /> Edit Assumptions</Button>}
      {hasOverrides && can("edit") && <Button variant="outline" size="sm" onClick={onClearOverrides} data-testid="clear-overrides-button"><RotateCcw size={14} className="mr-1.5" /> Reset Overrides</Button>}
      {can("export") && <Button size="sm" onClick={() => setSignKind("xlsx")} disabled={busy !== ""} data-testid="export-excel-button" className="bg-emerald-700 hover:bg-emerald-800"><FileSpreadsheet size={14} className="mr-1.5" /> {busy === "xlsx" ? "Preparing pack..." : "Download Excel"}</Button>}
      {can("export") && <Button variant="outline" size="sm" onClick={() => setSignKind("preview")} disabled={busy !== ""} data-testid="preview-pack-button"><Eye size={14} className="mr-1.5" /> Preview pack</Button>}
      {can("export") && <Button size="sm" onClick={() => setSignKind("pdf")} disabled={busy !== ""} data-testid="export-pdf-button" className="bg-slate-900 hover:bg-slate-700"><FileText size={14} className="mr-1.5" /> {busy === "pdf" ? "Preparing pack..." : "Download PDF"}</Button>}
      {project && can("export") && <span className={`inline-flex items-center gap-1 text-[11px] ${signed ? "text-emerald-700" : "text-amber-700"}`} data-testid="sign-off-status"><PenLine size={11} /> {signed ? "Signed off" : "Not signed off — issued as DRAFT"}</span>}
      <PackPreview projectId={projectId} open={preview} onClose={() => setPreview(false)} prepare={prepare} />
      {project && <SignOffDialog project={project} kind={signKind} open={!!signKind} onClose={() => setSignKind(null)} onConfirm={confirm} onChanged={onProjectChanged} />}
      {can("export") && <button type="button" onClick={() => dl("xlsx", true)} disabled={busy !== ""} className="text-[11px] text-slate-500 hover:text-slate-800 underline decoration-dotted" data-testid="export-detail-xlsx-button">{busy === "xlsx-detail" ? "Preparing..." : "Detailed workings (all sheets, Excel)"}</button>}
    </div>
  );
};
